// components/QuickEntryForm/QuickEntryForm.tsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';
import {
  Plus,
  BarChart3,
  Package,
  AlertTriangle,
  TrendingUp,
  Target,
  Truck,
  ClipboardList,
  Save,
  X,
  CheckCircle2,
  Database,
  FileText,
  Globe,
  Shield,
  Users,
  Building,
  Calendar,
  DollarSign,
  MapPin,
  Mail,
  Phone,
  Star,
  Zap,
  Leaf,
  Heart,
  Eye
} from 'lucide-react';

// Import the new Supabase service
import { quickEntryService } from '@/services/quickEntryService';

interface QuickEntryFormProps {
  onSuccess?: () => void;
}

export const QuickEntryForm = ({ onSuccess }: QuickEntryFormProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedDataType, setSelectedDataType] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [assessmentTemplates, setAssessmentTemplates] = useState<any[]>([]);
  const [assessmentCustomQuestions, setAssessmentCustomQuestions] = useState<any[]>([]);
  const [newAssessmentQuestion, setNewAssessmentQuestion] = useState({ label: '', type: 'text', options: '', required: false });
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<any[]>([]);

  // Load tags from database
  const loadAvailableTags = async () => {
    if (!user?.id) return;
    try {
      const { tagService } = await import('@/services/tagService');
      const allTags = await tagService.getAllTags(user.id);
      setAvailableTags(allTags);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  useEffect(() => {
    loadAvailableTags();
  }, [user?.id]);

  useEffect(() => {
    if (isFormOpen) {
      loadAvailableTags();
    }
  }, [isFormOpen]);

  // Form data for different data types
  const [esgMetricData, setEsgMetricData] = useState({
    name: '',
    category: '',
    subcategory: '',
    description: '',
    value: '',
    unit: '',
    data_type: 'number',
    currency: 'USD',
    difficulty: 'Easy',
    calculation_method: '',
    data_source: 'Manual Entry',
    frequency: 'Annual',
    mandatory: false,
    scope: '',
    target_type: '',
    emission_factor: '',
    calorific_value: '',
    tags: '',
    source: '',
    reporting_period: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [materialData, setMaterialData] = useState({
    name: '',
    category: '',
    subcategory: '',
    description: '',
    unit: '',
    density: '',
    carbon_footprint: '',
    cost_per_unit: '',
    currency: 'USD',
    supplier_info: '',
    sustainability_rating: '',
    certifications: '',
    emission_factor: '',
    calorific_value: '',
    scope: '',
    tags: '',
    source: '',
    // Enhanced material properties
    environmental_impact_score: '',
    recyclability: '',
    biodegradability: '',
    availability: '',
    lead_time: '',
    minimum_order_quantity: '',
    material_type: '',
    composition: '',
    strength: '',
    durability: '',
    thermal_properties: '',
    chemical_resistance: '',
    reporting_period: new Date().toISOString().split('T')[0],
    data_source: 'Manual Entry',
    notes: ''
  });

  const [riskData, setRiskData] = useState({
    risk_name: '',
    risk_description: '',
    risk_category: 'operational',
    likelihood: 1,
    impact: 1,
    mitigation_strategy: '',
    responsible_person: '',
    review_date: '',
    status: 'active'
  });

  const [investmentData, setInvestmentData] = useState({
    investment_name: '',
    investment_type: 'equity',
    sector: '',
    region: '',
    investment_amount: '',
    currency: 'USD',
    esg_score: '',
    environmental_score: '',
    social_score: '',
    governance_score: '',
    carbon_intensity: '',
    water_intensity: '',
    waste_intensity: '',
    investment_date: new Date().toISOString().split('T')[0],
    last_reviewed: '',
    status: 'active',
    notes: ''
  });

  const [initiativeData, setInitiativeData] = useState({
    target_id: '',
    initiative_name: '',
    initiative_type: 'energy_efficiency',
    description: '',
    estimated_reduction: '',
    investment_required: '',
    currency: 'USD',
    implementation_date: new Date().toISOString().split('T')[0],
    completion_date: '',
    status: 'planned',
    actual_reduction: '',
    roi_percentage: '',
    responsible_team: '',
    priority_level: 'medium',
    outcome_summary: ''
  });

  const [supplierData, setSupplierData] = useState({
    supplier_name: '',
    supplier_type: 'tier_1',
    industry_sector: '',
    country: '',
    region: '',
    contact_email: '',
    contact_phone: '',
    annual_spend: '',
    currency: 'USD',
    esg_assessment_score: '',
    carbon_footprint: '',
    water_usage: '',
    waste_generation: '',
    labor_practices_score: '',
    environmental_compliance: false,
    social_compliance: false,
    governance_compliance: false,
    certification_standards: '',
    risk_level: 'medium',
    last_assessment_date: '',
    next_assessment_date: '',
    status: 'active',
    notes: ''
  });

  const [assessmentData, setAssessmentData] = useState({
    supplier_id: '',
    assessment_type: 'annual',
    assessment_date: new Date().toISOString().split('T')[0],
    assessor_name: '',
    overall_score: '',
    environmental_score: '',
    social_score: '',
    governance_score: '',
    findings: '',
    recommendations: '',
    action_items: '',
    follow_up_date: '',
    status: 'pending',
    notes: '',
    // Additional ESG fields
    has_ems: false,
    policies_environmental: false,
    policies_human_rights: false,
    policies_anti_corruption: false,
    data_privacy_policy: false,
    whistleblower_mechanism: false,
    sbti_committed: false,
    ghg_disclosed: false,
    ecovadis_score: '',
    cdp_score: '',
    scope1_emissions: '',
    scope2_emissions: '',
    scope3_emissions: '',
    energy_consumption_mwh: '',
    water_withdrawal_m3: '',
    waste_generated_tons: '',
    incidents_count: '',
    incidents_details: '',
    cap_in_place: false,
    cap_due_date: '',
  });

  const dataTypes = [
    {
      id: 'esg-metric',
      name: 'Organization ESG Metrics',
      description: 'Add environmental, social, and governance metrics',
      icon: BarChart3,
      color: 'bg-blue-500',
      form: 'esg-metric'
    },
    {
      id: 'material',
      name: 'Organization Material',
      description: 'Add materials with carbon footprint and sustainability data',
      icon: Package,
      color: 'bg-green-500',
      form: 'material'
    },
    {
      id: 'risk',
      name: 'Organization Risk',
      description: 'Add risk assessments and mitigation strategies',
      icon: AlertTriangle,
      color: 'bg-red-500',
      form: 'risk'
    },
    {
      id: 'investment',
      name: 'New Investment',
      description: 'Add investment portfolio data with ESG scores',
      icon: TrendingUp,
      color: 'bg-purple-500',
      form: 'investment'
    },
    {
      id: 'initiative',
      name: 'New Initiative',
      description: 'Add net-zero initiatives and decarbonization projects',
      icon: Target,
      color: 'bg-orange-500',
      form: 'initiative'
    },
    {
      id: 'supplier',
      name: 'New Supplier',
      description: 'Add supplier information and ESG compliance data',
      icon: Truck,
      color: 'bg-indigo-500',
      form: 'supplier'
    },
    {
      id: 'assessment',
      name: 'New Assessment for Supplier',
      description: 'Add supplier assessment and evaluation data',
      icon: ClipboardList,
      color: 'bg-teal-500',
      form: 'assessment'
    }
  ];

  const handleDataTypeSelect = (dataType: string) => {
    setSelectedDataType(dataType);
    setIsFormOpen(true);
  };

  // Load functions using Supabase
  const loadSuppliers = async () => {
    try {
      if (!user?.id) return;

      const suppliers = await quickEntryService.getSuppliers();

      setSupplierOptions(suppliers?.data || []);
    } catch (e) {
      console.error('Failed to load suppliers for assessment:', e);
      toast({
        title: 'Error',
        description: 'Failed to load suppliers',
        variant: 'destructive'
      });
    }
  };

  const loadAssessmentTemplates = async () => {
    try {
      if (!user?.id) return;

      const templates = await quickEntryService.getAssessmentTemplates();

      setAssessmentTemplates(templates?.data || []);
    } catch (e) {
      console.error('Failed to load assessment templates:', e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      switch (selectedDataType) {
        case 'esg-metric':
          await quickEntryService.createEsgMetric(esgMetricData);
          break;
        case 'material':
          await quickEntryService.createMaterial(materialData);
          break;
        case 'risk':
          await quickEntryService.createRiskAssessment(riskData);
          break;
        case 'investment':
          await quickEntryService.createInvestment(investmentData);
          break;
        case 'initiative':
          await quickEntryService.createInitiative(initiativeData);
          break;
        case 'supplier':
          await quickEntryService.createSupplier(supplierData);
          break;
        case 'assessment':
          await quickEntryService.createAssessment({
            ...assessmentData,
            custom_fields: assessmentCustomQuestions.map(q => ({
              label: q.label,
              type: q.type,
              value: q.value || null
            }))
          });
          break;
        default:
          throw new Error('Invalid data type');
      }

      toast({
        title: 'Success',
        description: 'Data added successfully!',
      });

      setIsFormOpen(false);
      resetForm();
      onSuccess?.();

    } catch (error) {
      console.error('Error submitting data:', error);
      toast({
        title: 'Error',
        description: 'Failed to add data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Template handlers using Supabase
  const handleApplyTemplate = async (templateId: string) => {
    try {
      const templateData = await quickEntryService.applyAssessmentTemplate(templateId);
      if (templateData) {
        // Extract the main assessment data
        const { id, user_id, created_at, updated_at, name, template_custom_questions, ...assessmentFields } = templateData;
        setAssessmentData(prev => ({ ...prev, ...assessmentFields }));

        // Handle custom questions if present
        if (Array.isArray(template_custom_questions)) {
          setAssessmentCustomQuestions(
            template_custom_questions.map((q: any) => ({
              ...q,
              value: q.default_value ?? ''
            }))
          );
        }

        toast({
          title: 'Template applied',
          description: 'Template data loaded successfully',
        });
      }
    } catch (error) {
      console.error('Error applying template:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply template',
        variant: 'destructive'
      });
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const name = prompt('Save current fields as template. Enter template name:');
      if (!name) return;

      const templateData = {
        ...assessmentData,
        template_custom_questions: assessmentCustomQuestions.map(q => ({
          label: q.label,
          type: q.type,
          options: q.options || '',
          required: !!q.required,
          default_value: q.value ?? ''
        }))
      };

      await quickEntryService.saveAssessmentTemplate(name, templateData);

      toast({
        title: 'Template saved',
        description: 'Template saved successfully',
      });

      // Reload templates
      loadAssessmentTemplates();

    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save template',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setEsgMetricData({
      name: '',
      category: '',
      subcategory: '',
      description: '',
      value: '',
      unit: '',
      data_type: 'number',
      currency: 'USD',
      difficulty: 'Easy',
      calculation_method: '',
      data_source: 'Manual Entry',
      frequency: 'Annual',
      mandatory: false,
      scope: '',
      target_type: '',
      emission_factor: '',
      calorific_value: '',
      tags: '',
      source: '',
      reporting_period: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setMaterialData({
      name: '',
      category: '',
      subcategory: '',
      description: '',
      unit: '',
      density: '',
      carbon_footprint: '',
      cost_per_unit: '',
      currency: 'USD',
      supplier_info: '',
      sustainability_rating: '',
      certifications: '',
      emission_factor: '',
      calorific_value: '',
      scope: '',
      tags: '',
      source: '',
      environmental_impact_score: '',
      recyclability: '',
      biodegradability: '',
      availability: '',
      lead_time: '',
      minimum_order_quantity: '',
      material_type: '',
      composition: '',
      strength: '',
      durability: '',
      thermal_properties: '',
      chemical_resistance: '',
      reporting_period: new Date().toISOString().split('T')[0],
      data_source: 'Manual Entry',
      notes: ''
    });
    setRiskData({
      risk_name: '',
      risk_description: '',
      risk_category: 'operational',
      likelihood: 1,
      impact: 1,
      mitigation_strategy: '',
      responsible_person: '',
      review_date: '',
      status: 'active',
      notes: ''
    });
    setInvestmentData({
      investment_name: '',
      investment_type: 'equity',
      sector: '',
      region: '',
      investment_amount: '',
      currency: 'USD',
      esg_score: '',
      environmental_score: '',
      social_score: '',
      governance_score: '',
      carbon_intensity: '',
      water_intensity: '',
      waste_intensity: '',
      investment_date: new Date().toISOString().split('T')[0],
      last_reviewed: '',
      status: 'active',
      notes: ''
    });
    setInitiativeData({
      target_id: '',
      initiative_name: '',
      initiative_type: 'energy_efficiency',
      description: '',
      estimated_reduction: '',
      investment_required: '',
      currency: 'USD',
      implementation_date: new Date().toISOString().split('T')[0],
      completion_date: '',
      status: 'planned',
      actual_reduction: '',
      roi_percentage: '',
      responsible_team: '',
      priority_level: 'medium',
      notes: '',
      outcome_summary: ''
    });
    setSupplierData({
      supplier_name: '',
      supplier_type: 'tier_1',
      industry_sector: '',
      country: '',
      region: '',
      contact_email: '',
      contact_phone: '',
      annual_spend: '',
      currency: 'USD',
      esg_assessment_score: '',
      carbon_footprint: '',
      water_usage: '',
      waste_generation: '',
      labor_practices_score: '',
      environmental_compliance: false,
      social_compliance: false,
      governance_compliance: false,
      certification_standards: '',
      risk_level: 'medium',
      last_assessment_date: '',
      next_assessment_date: '',
      status: 'active',
      notes: ''
    });
    setAssessmentData({
      supplier_id: '',
      assessment_type: 'annual',
      assessment_date: new Date().toISOString().split('T')[0],
      assessor_name: '',
      overall_score: '',
      environmental_score: '',
      social_score: '',
      governance_score: '',
      findings: '',
      recommendations: '',
      action_items: '',
      follow_up_date: '',
      status: 'pending',
      notes: '',
      has_ems: false,
      policies_environmental: false,
      policies_human_rights: false,
      policies_anti_corruption: false,
      data_privacy_policy: false,
      whistleblower_mechanism: false,
      sbti_committed: false,
      ghg_disclosed: false,
      ecovadis_score: '',
      cdp_score: '',
      scope1_emissions: '',
      scope2_emissions: '',
      scope3_emissions: '',
      energy_consumption_mwh: '',
      water_withdrawal_m3: '',
      waste_generated_tons: '',
      incidents_count: '',
      incidents_details: '',
      cap_in_place: false,
      cap_due_date: '',
    });
    setAssessmentCustomQuestions([]);
    setNewAssessmentQuestion({ label: '', type: 'text', options: '', required: false });
    setSelectedTagIds([]);
  };

  useEffect(() => {
    if (isFormOpen && selectedDataType === 'assessment') {
      loadSuppliers();
      loadAssessmentTemplates();
    }
  }, [isFormOpen, selectedDataType, user?.id]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-gray-900">Quick Entry</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Add different types of data to your organization's storage.
          Select the type of data you want to add and fill out the form.
        </p>
      </div>

      {/* Data Type Selection Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dataTypes.map((dataType) => {
          const IconComponent = dataType.icon;
          return (
            <Card
              key={dataType.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
              onClick={() => handleDataTypeSelect(dataType.id)}
            >
              <CardHeader className="text-center">
                <div className={`w-12 h-12 rounded-full ${dataType.color} flex items-center justify-center mx-auto mb-4`}>
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <CardTitle className="text-lg">{dataType.name}</CardTitle>
                <p className="text-sm text-gray-600">{dataType.description}</p>
              </CardHeader>
              <CardContent className="text-center">
                <Button variant="outline" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add {dataType.name.split(' ').pop()}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Form Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dataTypes.find(dt => dt.id === selectedDataType)?.icon &&
                React.createElement(dataTypes.find(dt => dt.id === selectedDataType)!.icon, { className: "h-5 w-5" })
              }
              Add {dataTypes.find(dt => dt.id === selectedDataType)?.name}
            </DialogTitle>
            <DialogDescription>
              Fill out the form below to add new data to your organization's storage.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {selectedDataType === 'esg-metric' && (
              <EsgMetricForm data={esgMetricData} setData={setEsgMetricData} selectedTagIds={selectedTagIds} setSelectedTagIds={setSelectedTagIds} availableTags={availableTags} />
            )}

            {selectedDataType === 'material' && (
              <MaterialForm data={materialData} setData={setMaterialData} selectedTagIds={selectedTagIds} setSelectedTagIds={setSelectedTagIds} availableTags={availableTags} />
            )}

            {selectedDataType === 'risk' && (
              <RiskForm data={riskData} setData={setRiskData} />
            )}

            {selectedDataType === 'investment' && (
              <InvestmentForm data={investmentData} setData={setInvestmentData} />
            )}

            {selectedDataType === 'initiative' && (
              <InitiativeForm data={initiativeData} setData={setInitiativeData} />
            )}

            {selectedDataType === 'supplier' && (
              <SupplierForm data={supplierData} setData={setSupplierData} />
            )}

            {selectedDataType === 'assessment' && (
              <AssessmentForm
                data={assessmentData}
                setData={setAssessmentData}
                suppliers={supplierOptions}
                onSupplierSelect={(supplier: any) => {
                  setAssessmentData(prev => ({
                    ...prev,
                    supplier_id: supplier?.id || '',
                    overall_score: supplier?.esg_assessment_score != null ? String(supplier.esg_assessment_score) : prev.overall_score
                  }));
                }}
                templates={assessmentTemplates}
                onApplyTemplate={handleApplyTemplate}
                onSaveTemplate={handleSaveTemplate}
                customQuestions={assessmentCustomQuestions}
                onCustomQuestionsChange={setAssessmentCustomQuestions}
                newQuestion={newAssessmentQuestion}
                setNewQuestion={setNewAssessmentQuestion}
              />
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFormOpen(false)}
                disabled={loading}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving data...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Data
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Form Components
const EsgMetricForm = ({ data, setData, selectedTagIds, setSelectedTagIds, availableTags }: { data: any; setData: (data: any) => void; selectedTagIds: string[]; setSelectedTagIds: (tagIds: string[]) => void; availableTags: any[] }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="name">Metric Name *</Label>
        <Input
          id="name"
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          placeholder="e.g., Energy Consumption"
          required
        />
      </div>
      <div>
        <Label htmlFor="category">Category *</Label>
        <Select value={data.category} onValueChange={(value) => setData({ ...data, category: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Environmental">Environmental</SelectItem>
            <SelectItem value="Social">Social</SelectItem>
            <SelectItem value="Governance">Governance</SelectItem>
            <SelectItem value="Economic">Economic</SelectItem>
            <SelectItem value="Material">Material</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="subcategory">Subcategory *</Label>
        <Input
          id="subcategory"
          value={data.subcategory}
          onChange={(e) => setData({ ...data, subcategory: e.target.value })}
          placeholder="e.g., Energy, Waste, Water"
          required
        />
      </div>
      <div>
        <Label htmlFor="data_type">Data Type</Label>
        <Select value={data.data_type} onValueChange={(value) => setData({ ...data, data_type: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="percentage">Percentage</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="currency">Currency</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="value">Value *</Label>
        <Input
          id="value"
          type="number"
          step="0.01"
          value={data.value}
          onChange={(e) => setData({ ...data, value: e.target.value })}
          placeholder="Enter value"
          required
        />
      </div>
      <div>
        <Label htmlFor="unit">Unit *</Label>
        <Input
          id="unit"
          value={data.unit}
          onChange={(e) => setData({ ...data, unit: e.target.value })}
          placeholder="e.g., kWh, kg CO2e"
          required
        />
      </div>
      <div>
        <Label htmlFor="currency">Currency</Label>
        <Select value={data.currency} onValueChange={(value) => setData({ ...data, currency: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
            <SelectItem value="JPY">JPY</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="difficulty">Difficulty</Label>
        <Select value={data.difficulty} onValueChange={(value) => setData({ ...data, difficulty: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Easy">Easy</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Hard">Hard</SelectItem>
            <SelectItem value="Expert">Expert</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="scope">Scope</Label>
        <Select value={data.scope} onValueChange={(value) => setData({ ...data, scope: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select scope" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Scope 1">Scope 1</SelectItem>
            <SelectItem value="Scope 2">Scope 2</SelectItem>
            <SelectItem value="Scope 3">Scope 3</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="frequency">Frequency</Label>
        <Select value={data.frequency} onValueChange={(value) => setData({ ...data, frequency: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Daily">Daily</SelectItem>
            <SelectItem value="Weekly">Weekly</SelectItem>
            <SelectItem value="Monthly">Monthly</SelectItem>
            <SelectItem value="Quarterly">Quarterly</SelectItem>
            <SelectItem value="Annual">Annual</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="emission_factor">Emission Factor</Label>
        <Input
          id="emission_factor"
          type="number"
          step="0.000001"
          value={data.emission_factor}
          onChange={(e) => setData({ ...data, emission_factor: e.target.value })}
          placeholder="Enter emission factor"
        />
      </div>
      <div>
        <Label htmlFor="calorific_value">Calorific Value</Label>
        <Input
          id="calorific_value"
          type="number"
          step="0.000001"
          value={data.calorific_value}
          onChange={(e) => setData({ ...data, calorific_value: e.target.value })}
          placeholder="Enter calorific value"
        />
      </div>
      <div>
        <Label htmlFor="reporting_period">Reporting Period</Label>
        <Input
          id="reporting_period"
          type="date"
          value={data.reporting_period}
          onChange={(e) => setData({ ...data, reporting_period: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="source">Source</Label>
        <Input
          id="source"
          value={data.source}
          onChange={(e) => setData({ ...data, source: e.target.value })}
          placeholder="Data source or reference"
        />
      </div>
    </div>

    <div>
      <Label htmlFor="description">Description</Label>
      <Textarea
        id="description"
        value={data.description}
        onChange={(e) => setData({ ...data, description: e.target.value })}
        placeholder="Detailed description of the metric"
        rows={3}
      />
    </div>

    <div>
      <Label htmlFor="calculation_method">Calculation Method</Label>
      <Textarea
        id="calculation_method"
        value={data.calculation_method}
        onChange={(e) => setData({ ...data, calculation_method: e.target.value })}
        placeholder="How this metric is calculated"
        rows={2}
      />
    </div>

    <div>
      <Label className="text-sm font-medium">Tags</Label>
      <p className="text-xs text-gray-600 mb-2">
        Add tags to organize and categorize this metric for better analysis and filtering
      </p>
      {selectedTagIds.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-green-700">
            ✓ {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedTagIds.map((tagId) => {
              const tag = availableTags.find(t => t.id === tagId);
              return tag ? (
                <Badge
                  key={tagId}
                  variant="secondary"
                  className="flex items-center gap-2 px-2 py-1 text-xs"
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="truncate">{tag.name}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedTagIds(prev => prev.filter(id => id !== tagId))}
                    className="ml-2 p-1"
                    aria-label={`Remove tag ${tag.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null;
            })}
          </div>
        </div>
      )}
      <DataTaggingComponent
        selectedTags={selectedTagIds}
        onTagsChange={(tags) => setSelectedTagIds(tags)}
        dataType="metric"
        showCreateTag={true}
      />
    </div>

    <div>
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={data.notes}
        onChange={(e) => setData({ ...data, notes: e.target.value })}
        placeholder="Additional notes or context"
        rows={3}
      />
    </div>
  </div>
);

const MaterialForm = ({ data, setData, selectedTagIds, setSelectedTagIds, availableTags }: { data: any; setData: (data: any) => void; selectedTagIds: string[]; setSelectedTagIds: (tagIds: string[]) => void; availableTags: any[] }) => (
  <div className="space-y-6">
    {/* Basic Information */}
    <div className="bg-blue-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-blue-900 mb-4">Basic Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Material Name *</Label>
          <Input
            id="name"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            placeholder="e.g., Steel, Aluminum"
            required
          />
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <Select value={data.category} onValueChange={(value) => setData({ ...data, category: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Metals">Metals</SelectItem>
              <SelectItem value="Plastics">Plastics</SelectItem>
              <SelectItem value="Chemicals">Chemicals</SelectItem>
              <SelectItem value="Textiles">Textiles</SelectItem>
              <SelectItem value="Ceramics">Ceramics</SelectItem>
              <SelectItem value="Composites">Composites</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="subcategory">Subcategory</Label>
          <Input
            id="subcategory"
            value={data.subcategory}
            onChange={(e) => setData({ ...data, subcategory: e.target.value })}
            placeholder="e.g., Ferrous, Non-ferrous"
          />
        </div>
        <div>
          <Label htmlFor="unit">Unit</Label>
          <Input
            id="unit"
            value={data.unit}
            onChange={(e) => setData({ ...data, unit: e.target.value })}
            placeholder="e.g., kg, m³, pieces"
          />
        </div>
      </div>
    </div>

    {/* Physical Properties */}
    <div className="bg-green-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-green-900 mb-4">Physical Properties</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="density">Density (kg/m³)</Label>
          <Input
            id="density"
            type="number"
            step="0.0001"
            value={data.density}
            onChange={(e) => setData({ ...data, density: e.target.value })}
            placeholder="Enter density"
          />
        </div>
        <div>
          <Label htmlFor="carbon_footprint">Carbon Footprint (kg CO2e/kg)</Label>
          <Input
            id="carbon_footprint"
            type="number"
            step="0.0001"
            value={data.carbon_footprint}
            onChange={(e) => setData({ ...data, carbon_footprint: e.target.value })}
            placeholder="Enter carbon footprint"
          />
        </div>
        <div>
          <Label htmlFor="cost_per_unit">Cost per Unit</Label>
          <Input
            id="cost_per_unit"
            type="number"
            step="0.01"
            value={data.cost_per_unit}
            onChange={(e) => setData({ ...data, cost_per_unit: e.target.value })}
            placeholder="Enter cost"
          />
        </div>
        <div>
          <Label htmlFor="currency">Currency</Label>
          <Select value={data.currency} onValueChange={(value) => setData({ ...data, currency: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="JPY">JPY</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    {/* Environmental Properties */}
    <div className="bg-purple-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-purple-900 mb-4">Environmental Properties</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="environmental_impact_score">Environmental Impact Score (0-100)</Label>
          <Input
            id="environmental_impact_score"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={data.environmental_impact_score}
            onChange={(e) => setData({ ...data, environmental_impact_score: e.target.value })}
            placeholder="Enter score"
          />
        </div>
        <div>
          <Label htmlFor="recyclability">Recyclability</Label>
          <Select value={data.recyclability} onValueChange={(value) => setData({ ...data, recyclability: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select recyclability" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Not Recyclable">Not Recyclable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="biodegradability">Biodegradability</Label>
          <Select value={data.biodegradability} onValueChange={(value) => setData({ ...data, biodegradability: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select biodegradability" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Biodegradable">Biodegradable</SelectItem>
              <SelectItem value="Partially Biodegradable">Partially Biodegradable</SelectItem>
              <SelectItem value="Non-Biodegradable">Non-Biodegradable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="scope">Scope</Label>
          <Select value={data.scope} onValueChange={(value) => setData({ ...data, scope: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Scope 1">Scope 1</SelectItem>
              <SelectItem value="Scope 2">Scope 2</SelectItem>
              <SelectItem value="Scope 3">Scope 3</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    {/* Supply Chain Properties */}
    <div className="bg-orange-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-orange-900 mb-4">Supply Chain Properties</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="availability">Availability</Label>
          <Select value={data.availability} onValueChange={(value) => setData({ ...data, availability: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select availability" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Readily Available">Readily Available</SelectItem>
              <SelectItem value="Limited Availability">Limited Availability</SelectItem>
              <SelectItem value="Special Order">Special Order</SelectItem>
              <SelectItem value="Discontinued">Discontinued</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="lead_time">Lead Time</Label>
          <Input
            id="lead_time"
            value={data.lead_time}
            onChange={(e) => setData({ ...data, lead_time: e.target.value })}
            placeholder="e.g., 2-4 weeks"
          />
        </div>
        <div>
          <Label htmlFor="minimum_order_quantity">Minimum Order Quantity</Label>
          <Input
            id="minimum_order_quantity"
            value={data.minimum_order_quantity}
            onChange={(e) => setData({ ...data, minimum_order_quantity: e.target.value })}
            placeholder="e.g., 1000 kg"
          />
        </div>
        <div>
          <Label htmlFor="sustainability_rating">Sustainability Rating (1-5)</Label>
          <Input
            id="sustainability_rating"
            type="number"
            min="1"
            max="5"
            value={data.sustainability_rating}
            onChange={(e) => setData({ ...data, sustainability_rating: e.target.value })}
            placeholder="Enter rating"
          />
        </div>
      </div>
    </div>

    {/* Technical Properties */}
    <div className="bg-teal-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-teal-900 mb-4">Technical Properties</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="material_type">Material Type</Label>
          <Input
            id="material_type"
            value={data.material_type}
            onChange={(e) => setData({ ...data, material_type: e.target.value })}
            placeholder="e.g., Light Metal, Ferrous Metal"
          />
        </div>
        <div>
          <Label htmlFor="composition">Composition</Label>
          <Input
            id="composition"
            value={data.composition}
            onChange={(e) => setData({ ...data, composition: e.target.value })}
            placeholder="e.g., Iron-carbon alloy"
          />
        </div>
        <div>
          <Label htmlFor="strength">Strength</Label>
          <Input
            id="strength"
            value={data.strength}
            onChange={(e) => setData({ ...data, strength: e.target.value })}
            placeholder="e.g., High tensile strength"
          />
        </div>
        <div>
          <Label htmlFor="durability">Durability</Label>
          <Input
            id="durability"
            value={data.durability}
            onChange={(e) => setData({ ...data, durability: e.target.value })}
            placeholder="e.g., Excellent corrosion resistance"
          />
        </div>
        <div>
          <Label htmlFor="thermal_properties">Thermal Properties</Label>
          <Input
            id="thermal_properties"
            value={data.thermal_properties}
            onChange={(e) => setData({ ...data, thermal_properties: e.target.value })}
            placeholder="e.g., High thermal conductivity"
          />
        </div>
        <div>
          <Label htmlFor="chemical_resistance">Chemical Resistance</Label>
          <Input
            id="chemical_resistance"
            value={data.chemical_resistance}
            onChange={(e) => setData({ ...data, chemical_resistance: e.target.value })}
            placeholder="e.g., Resistant to acids"
          />
        </div>
      </div>
    </div>

    {/* Additional Information */}
    <div className="bg-gray-50 p-4 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
      <div className="space-y-4">
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            placeholder="Detailed description of the material"
            rows={3}
          />
        </div>
        <div>
          <Label htmlFor="supplier_info">Supplier Information</Label>
          <Input
            id="supplier_info"
            value={data.supplier_info}
            onChange={(e) => setData({ ...data, supplier_info: e.target.value })}
            placeholder="Supplier details"
          />
        </div>
        <div>
          <Label htmlFor="certifications">Certifications</Label>
          <Input
            id="certifications"
            value={data.certifications}
            onChange={(e) => setData({ ...data, certifications: e.target.value })}
            placeholder="Comma-separated certifications"
          />
        </div>
        <div>
          <Label htmlFor="emission_factor">Emission Factor</Label>
          <Input
            id="emission_factor"
            type="number"
            step="0.000001"
            value={data.emission_factor}
            onChange={(e) => setData({ ...data, emission_factor: e.target.value })}
            placeholder="Enter emission factor"
          />
        </div>
        <div>
          <Label htmlFor="calorific_value">Calorific Value</Label>
          <Input
            id="calorific_value"
            type="number"
            step="0.000001"
            value={data.calorific_value}
            onChange={(e) => setData({ ...data, calorific_value: e.target.value })}
            placeholder="Enter calorific value"
          />
        </div>
        <div>
          <Label className="text-sm font-medium">Tags</Label>
          <p className="text-xs text-gray-600 mb-2">
            Add tags to organize and categorize this material for better analysis and filtering
          </p>
          {selectedTagIds.length > 0 && (
            <div className="space-y-1 mb-3">
              <p className="text-sm font-medium text-green-700">
                ✓ {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedTagIds.map((tagId) => {
                  const tag = availableTags.find(t => t.id === tagId);
                  return tag ? (
                    <Badge
                      key={tagId}
                      variant="secondary"
                      className="flex items-center gap-2 px-2 py-1 text-xs"
                    >
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="truncate">{tag.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedTagIds(prev => prev.filter(id => id !== tagId))}
                        className="ml-2 p-1"
                        aria-label={`Remove tag ${tag.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          )}
          <DataTaggingComponent
            selectedTags={selectedTagIds}
            onTagsChange={(tags) => setSelectedTagIds(tags)}
            dataType="material"
            showCreateTag={true}
          />
        </div>
        <div>
          <Label htmlFor="source">Source</Label>
          <Input
            id="source"
            value={data.source}
            onChange={(e) => setData({ ...data, source: e.target.value })}
            placeholder="Data source or reference"
          />
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            placeholder="Additional notes or context"
            rows={3}
          />
        </div>
      </div>
    </div>
  </div>
);

// Placeholder components for other forms
const RiskForm = ({ data, setData }: { data: any; setData: (data: any) => void }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="risk_name">Risk Name *</Label>
        <Input
          id="risk_name"
          value={data.risk_name}
          onChange={(e) => setData({ ...data, risk_name: e.target.value })}
          placeholder="Enter risk name"
          required
        />
      </div>
      <div>
        <Label htmlFor="risk_category">Risk Category *</Label>
        <Select value={data.risk_category} onValueChange={(value) => setData({ ...data, risk_category: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="climate">Climate</SelectItem>
            <SelectItem value="regulatory">Regulatory</SelectItem>
            <SelectItem value="operational">Operational</SelectItem>
            <SelectItem value="financial">Financial</SelectItem>
            <SelectItem value="reputational">Reputational</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="likelihood">Likelihood (1-5) *</Label>
        <Select value={data.likelihood.toString()} onValueChange={(value) => setData({ ...data, likelihood: parseInt(value) })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 - Very Low</SelectItem>
            <SelectItem value="2">2 - Low</SelectItem>
            <SelectItem value="3">3 - Medium</SelectItem>
            <SelectItem value="4">4 - High</SelectItem>
            <SelectItem value="5">5 - Very High</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="impact">Impact (1-5) *</Label>
        <Select value={data.impact.toString()} onValueChange={(value) => setData({ ...data, impact: parseInt(value) })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 - Very Low</SelectItem>
            <SelectItem value="2">2 - Low</SelectItem>
            <SelectItem value="3">3 - Medium</SelectItem>
            <SelectItem value="4">4 - High</SelectItem>
            <SelectItem value="5">5 - Very High</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="responsible_person">Responsible Person</Label>
        <Input
          id="responsible_person"
          value={data.responsible_person}
          onChange={(e) => setData({ ...data, responsible_person: e.target.value })}
          placeholder="Enter responsible person"
        />
      </div>
      <div>
        <Label htmlFor="review_date">Review Date</Label>
        <Input
          id="review_date"
          type="date"
          value={data.review_date}
          onChange={(e) => setData({ ...data, review_date: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={data.status} onValueChange={(value) => setData({ ...data, status: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="mitigated">Mitigated</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div>
      <Label htmlFor="risk_description">Risk Description *</Label>
      <Textarea
        id="risk_description"
        value={data.risk_description}
        onChange={(e) => setData({ ...data, risk_description: e.target.value })}
        placeholder="Describe the risk in detail"
        rows={3}
        required
      />
    </div>

    <div>
      <Label htmlFor="mitigation_strategy">Mitigation Strategy</Label>
      <Textarea
        id="mitigation_strategy"
        value={data.mitigation_strategy}
        onChange={(e) => setData({ ...data, mitigation_strategy: e.target.value })}
        placeholder="Describe the mitigation strategy"
        rows={3}
      />
    </div>
  </div>
);

const InvestmentForm = ({ data, setData }: { data: any; setData: (data: any) => void }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="investment_name">Investment Name *</Label>
        <Input
          id="investment_name"
          value={data.investment_name}
          onChange={(e) => setData({ ...data, investment_name: e.target.value })}
          placeholder="Enter investment name"
          required
        />
      </div>
      <div>
        <Label htmlFor="investment_type">Investment Type *</Label>
        <Select value={data.investment_type} onValueChange={(value) => setData({ ...data, investment_type: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="equity">Equity</SelectItem>
            <SelectItem value="bond">Bond</SelectItem>
            <SelectItem value="fund">Fund</SelectItem>
            <SelectItem value="real_estate">Real Estate</SelectItem>
            <SelectItem value="alternative">Alternative</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="sector">Sector</Label>
        <Input
          id="sector"
          value={data.sector}
          onChange={(e) => setData({ ...data, sector: e.target.value })}
          placeholder="e.g., Technology, Healthcare"
        />
      </div>
      <div>
        <Label htmlFor="region">Region</Label>
        <Input
          id="region"
          value={data.region}
          onChange={(e) => setData({ ...data, region: e.target.value })}
          placeholder="e.g., North America, Europe"
        />
      </div>
      <div>
        <Label htmlFor="investment_amount">Investment Amount *</Label>
        <Input
          id="investment_amount"
          type="number"
          step="0.01"
          value={data.investment_amount}
          onChange={(e) => setData({ ...data, investment_amount: e.target.value })}
          placeholder="Enter amount"
          required
        />
      </div>
      <div>
        <Label htmlFor="currency">Currency</Label>
        <Select value={data.currency} onValueChange={(value) => setData({ ...data, currency: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
            <SelectItem value="JPY">JPY</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="esg_score">ESG Score (0-100)</Label>
        <Input
          id="esg_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.esg_score}
          onChange={(e) => setData({ ...data, esg_score: e.target.value })}
          placeholder="Enter ESG score"
        />
      </div>
      <div>
        <Label htmlFor="environmental_score">Environmental Score (0-100)</Label>
        <Input
          id="environmental_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.environmental_score}
          onChange={(e) => setData({ ...data, environmental_score: e.target.value })}
          placeholder="Enter environmental score"
        />
      </div>
      <div>
        <Label htmlFor="social_score">Social Score (0-100)</Label>
        <Input
          id="social_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.social_score}
          onChange={(e) => setData({ ...data, social_score: e.target.value })}
          placeholder="Enter social score"
        />
      </div>
      <div>
        <Label htmlFor="governance_score">Governance Score (0-100)</Label>
        <Input
          id="governance_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.governance_score}
          onChange={(e) => setData({ ...data, governance_score: e.target.value })}
          placeholder="Enter governance score"
        />
      </div>
      <div>
        <Label htmlFor="carbon_intensity">Carbon Intensity (tCO2e/million revenue)</Label>
        <Input
          id="carbon_intensity"
          type="number"
          step="0.01"
          value={data.carbon_intensity}
          onChange={(e) => setData({ ...data, carbon_intensity: e.target.value })}
          placeholder="Enter carbon intensity"
        />
      </div>
      <div>
        <Label htmlFor="water_intensity">Water Intensity</Label>
        <Input
          id="water_intensity"
          type="number"
          step="0.01"
          value={data.water_intensity}
          onChange={(e) => setData({ ...data, water_intensity: e.target.value })}
          placeholder="Enter water intensity"
        />
      </div>
      <div>
        <Label htmlFor="waste_intensity">Waste Intensity</Label>
        <Input
          id="waste_intensity"
          type="number"
          step="0.01"
          value={data.waste_intensity}
          onChange={(e) => setData({ ...data, waste_intensity: e.target.value })}
          placeholder="Enter waste intensity"
        />
      </div>
      <div>
        <Label htmlFor="investment_date">Investment Date *</Label>
        <Input
          id="investment_date"
          type="date"
          value={data.investment_date}
          onChange={(e) => setData({ ...data, investment_date: e.target.value })}
          required
        />
      </div>
      <div>
        <Label htmlFor="last_reviewed">Last Reviewed</Label>
        <Input
          id="last_reviewed"
          type="date"
          value={data.last_reviewed}
          onChange={(e) => setData({ ...data, last_reviewed: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={data.status} onValueChange={(value) => setData({ ...data, status: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="divested">Divested</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div>
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={data.notes}
        onChange={(e) => setData({ ...data, notes: e.target.value })}
        placeholder="Additional notes about this investment"
        rows={3}
      />
    </div>
  </div>
);

const InitiativeForm = ({ data, setData }: { data: any; setData: (data: any) => void }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="initiative_name">Initiative Name *</Label>
        <Input
          id="initiative_name"
          value={data.initiative_name}
          onChange={(e) => setData({ ...data, initiative_name: e.target.value })}
          placeholder="Enter initiative name"
          required
        />
      </div>
      <div>
        <Label htmlFor="initiative_type">Initiative Type *</Label>
        <Select value={data.initiative_type} onValueChange={(value) => setData({ ...data, initiative_type: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="energy_efficiency">Energy Efficiency</SelectItem>
            <SelectItem value="renewable_energy">Renewable Energy</SelectItem>
            <SelectItem value="process_improvement">Process Improvement</SelectItem>
            <SelectItem value="fuel_switching">Fuel Switching</SelectItem>
            <SelectItem value="carbon_capture">Carbon Capture</SelectItem>
            <SelectItem value="nature_based">Nature Based</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="estimated_reduction">Estimated Reduction (tCO2e/year) *</Label>
        <Input
          id="estimated_reduction"
          type="number"
          step="0.01"
          value={data.estimated_reduction}
          onChange={(e) => setData({ ...data, estimated_reduction: e.target.value })}
          placeholder="Enter estimated reduction"
          required
        />
      </div>
      <div>
        <Label htmlFor="investment_required">Investment Required</Label>
        <Input
          id="investment_required"
          type="number"
          step="0.01"
          value={data.investment_required}
          onChange={(e) => setData({ ...data, investment_required: e.target.value })}
          placeholder="Enter investment amount"
        />
      </div>
      <div>
        <Label htmlFor="currency">Currency</Label>
        <Select value={data.currency} onValueChange={(value) => setData({ ...data, currency: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
            <SelectItem value="JPY">JPY</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="implementation_date">Implementation Date</Label>
        <Input
          id="implementation_date"
          type="date"
          value={data.implementation_date}
          onChange={(e) => setData({ ...data, implementation_date: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="completion_date">Target Completion Date</Label>
        <Input
          id="completion_date"
          type="date"
          value={data.completion_date}
          onChange={(e) => setData({ ...data, completion_date: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={data.status} onValueChange={(value) => setData({ ...data, status: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="actual_reduction">Actual Reduction (tCO2e/year)</Label>
        <Input
          id="actual_reduction"
          type="number"
          step="0.01"
          value={data.actual_reduction}
          onChange={(e) => setData({ ...data, actual_reduction: e.target.value })}
          placeholder="Enter actual reduction"
        />
      </div>
      <div>
        <Label htmlFor="roi_percentage">ROI Percentage</Label>
        <Input
          id="roi_percentage"
          type="number"
          step="0.01"
          value={data.roi_percentage}
          onChange={(e) => setData({ ...data, roi_percentage: e.target.value })}
          placeholder="Enter ROI percentage"
        />
      </div>
      <div>
        <Label htmlFor="responsible_team">Responsible Team</Label>
        <Input
          id="responsible_team"
          value={data.responsible_team}
          onChange={(e) => setData({ ...data, responsible_team: e.target.value })}
          placeholder="Enter responsible team"
        />
      </div>
      <div>
        <Label htmlFor="priority_level">Priority Level</Label>
        <Select value={data.priority_level} onValueChange={(value) => setData({ ...data, priority_level: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div>
      <Label htmlFor="description">Description</Label>
      <Textarea
        id="description"
        value={data.description}
        onChange={(e) => setData({ ...data, description: e.target.value })}
        placeholder="Describe the initiative in detail"
        rows={3}
      />
    </div>

    {/* <div>
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={data.notes}
        onChange={(e) => setData({ ...data, notes: e.target.value })}
        placeholder="Additional notes or context"
        rows={3}
      />
    </div> */}

    <div>
      <Label htmlFor="outcome_summary">Outcome Summary</Label>
      <Textarea
        id="outcome_summary"
        value={data.outcome_summary}
        onChange={(e) => setData({ ...data, outcome_summary: e.target.value })}
        placeholder="Summarize the outcomes and achievements of this initiative for ESG reporting"
        rows={3}
      />
    </div>
  </div>
);

const SupplierForm = ({ data, setData }: { data: any; setData: (data: any) => void }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="supplier_name">Supplier Name *</Label>
        <Input
          id="supplier_name"
          value={data.supplier_name}
          onChange={(e) => setData({ ...data, supplier_name: e.target.value })}
          placeholder="Enter supplier name"
          required
        />
      </div>
      <div>
        <Label htmlFor="supplier_type">Supplier Type *</Label>
        <Select value={data.supplier_type} onValueChange={(value) => setData({ ...data, supplier_type: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tier_1">Tier 1</SelectItem>
            <SelectItem value="tier_2">Tier 2</SelectItem>
            <SelectItem value="tier_3">Tier 3</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="non_critical">Non-Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="industry_sector">Industry Sector</Label>
        <Input
          id="industry_sector"
          value={data.industry_sector}
          onChange={(e) => setData({ ...data, industry_sector: e.target.value })}
          placeholder="e.g., Manufacturing, Technology"
        />
      </div>
      <div>
        <Label htmlFor="country">Country</Label>
        <Input
          id="country"
          value={data.country}
          onChange={(e) => setData({ ...data, country: e.target.value })}
          placeholder="Enter country"
        />
      </div>
      <div>
        <Label htmlFor="region">Region</Label>
        <Input
          id="region"
          value={data.region}
          onChange={(e) => setData({ ...data, region: e.target.value })}
          placeholder="Enter region"
        />
      </div>
      <div>
        <Label htmlFor="contact_email">Contact Email</Label>
        <Input
          id="contact_email"
          type="email"
          value={data.contact_email}
          onChange={(e) => setData({ ...data, contact_email: e.target.value })}
          placeholder="Enter contact email"
        />
      </div>
      <div>
        <Label htmlFor="contact_phone">Contact Phone</Label>
        <Input
          id="contact_phone"
          value={data.contact_phone}
          onChange={(e) => setData({ ...data, contact_phone: e.target.value })}
          placeholder="Enter contact phone"
        />
      </div>
      <div>
        <Label htmlFor="annual_spend">Annual Spend</Label>
        <Input
          id="annual_spend"
          type="number"
          step="0.01"
          value={data.annual_spend}
          onChange={(e) => setData({ ...data, annual_spend: e.target.value })}
          placeholder="Enter annual spend"
        />
      </div>
      <div>
        <Label htmlFor="currency">Currency</Label>
        <Select value={data.currency} onValueChange={(value) => setData({ ...data, currency: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
            <SelectItem value="JPY">JPY</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="esg_assessment_score">ESG Assessment Score (0-100)</Label>
        <Input
          id="esg_assessment_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.esg_assessment_score}
          onChange={(e) => setData({ ...data, esg_assessment_score: e.target.value })}
          placeholder="Enter ESG score"
        />
      </div>
      <div>
        <Label htmlFor="carbon_footprint">Carbon Footprint (tCO2e)</Label>
        <Input
          id="carbon_footprint"
          type="number"
          step="0.01"
          value={data.carbon_footprint}
          onChange={(e) => setData({ ...data, carbon_footprint: e.target.value })}
          placeholder="Enter carbon footprint"
        />
      </div>
      <div>
        <Label htmlFor="water_usage">Water Usage</Label>
        <Input
          id="water_usage"
          type="number"
          step="0.01"
          value={data.water_usage}
          onChange={(e) => setData({ ...data, water_usage: e.target.value })}
          placeholder="Enter water usage"
        />
      </div>
      <div>
        <Label htmlFor="waste_generation">Waste Generation</Label>
        <Input
          id="waste_generation"
          type="number"
          step="0.01"
          value={data.waste_generation}
          onChange={(e) => setData({ ...data, waste_generation: e.target.value })}
          placeholder="Enter waste generation"
        />
      </div>
      <div>
        <Label htmlFor="labor_practices_score">Labor Practices Score (0-100)</Label>
        <Input
          id="labor_practices_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.labor_practices_score}
          onChange={(e) => setData({ ...data, labor_practices_score: e.target.value })}
          placeholder="Enter labor practices score"
        />
      </div>
      <div>
        <Label htmlFor="risk_level">Risk Level</Label>
        <Select value={data.risk_level} onValueChange={(value) => setData({ ...data, risk_level: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="last_assessment_date">Last Assessment Date</Label>
        <Input
          id="last_assessment_date"
          type="date"
          value={data.last_assessment_date}
          onChange={(e) => setData({ ...data, last_assessment_date: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="next_assessment_date">Next Assessment Date</Label>
        <Input
          id="next_assessment_date"
          type="date"
          value={data.next_assessment_date}
          onChange={(e) => setData({ ...data, next_assessment_date: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={data.status} onValueChange={(value) => setData({ ...data, status: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="terminated">Terminated</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="space-y-4">
      <Label>Compliance Status</Label>
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Switch
            checked={data.environmental_compliance}
            onCheckedChange={(checked) => setData({ ...data, environmental_compliance: checked })}
          />
          <Label>Environmental</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            checked={data.social_compliance}
            onCheckedChange={(checked) => setData({ ...data, social_compliance: checked })}
          />
          <Label>Social</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            checked={data.governance_compliance}
            onCheckedChange={(checked) => setData({ ...data, governance_compliance: checked })}
          />
          <Label>Governance</Label>
        </div>
      </div>
    </div>

    <div>
      <Label htmlFor="certification_standards">Certification Standards</Label>
      <Input
        id="certification_standards"
        value={data.certification_standards}
        onChange={(e) => setData({ ...data, certification_standards: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
        placeholder="e.g., ISO14001, SA8000 (comma-separated)"
      />
    </div>

    <div>
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={data.notes}
        onChange={(e) => setData({ ...data, notes: e.target.value })}
        placeholder="Additional notes or context"
        rows={3}
      />
    </div>
  </div>
);

const AssessmentForm = ({ data, setData, suppliers, onSupplierSelect, templates, onApplyTemplate, onSaveTemplate, customQuestions, onCustomQuestionsChange, newQuestion, setNewQuestion }: { data: any; setData: (data: any) => void; suppliers: any[]; onSupplierSelect: (supplier: any) => void; templates: any[]; onApplyTemplate: (templateId: string) => Promise<void>; onSaveTemplate: () => Promise<void>; customQuestions: any[]; onCustomQuestionsChange: (questions: any[]) => void; newQuestion: any; setNewQuestion: (question: any) => void }) => (
  <div className="space-y-6">
    {/* Template controls */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="md:col-span-2">
        <Label htmlFor="template">Template</Label>
        <Select onValueChange={onApplyTemplate}>
          <SelectTrigger>
            <SelectValue placeholder="Select saved template (optional)" />
          </SelectTrigger>
          <SelectContent>
            {templates?.map((t: any) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end">
        <Button type="button" variant="outline" onClick={onSaveTemplate}>Save as Template</Button>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="supplier_id">Supplier</Label>
        <Select
          value={data.supplier_id}
          onValueChange={(value) => {
            setData({ ...data, supplier_id: value });
            const s = suppliers?.find((sup: any) => sup.id === value);
            if (s) onSupplierSelect(s);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select supplier" />
          </SelectTrigger>
          <SelectContent>
            {suppliers?.map((s: any) => (
              <SelectItem key={s.id} value={s.id}>{s.supplier_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="assessment_type">Assessment Type *</Label>
        <Select value={data.assessment_type} onValueChange={(value) => setData({ ...data, assessment_type: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="initial">Initial</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
            <SelectItem value="audit">Audit</SelectItem>
            <SelectItem value="incident_based">Incident Based</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="assessment_date">Assessment Date *</Label>
        <Input
          id="assessment_date"
          type="date"
          value={data.assessment_date}
          onChange={(e) => setData({ ...data, assessment_date: e.target.value })}
          required
        />
      </div>
      <div>
        <Label htmlFor="assessor_name">Assessor Name</Label>
        <Input
          id="assessor_name"
          value={data.assessor_name}
          onChange={(e) => setData({ ...data, assessor_name: e.target.value })}
          placeholder="Enter assessor name"
        />
      </div>
      <div>
        <Label htmlFor="overall_score">Overall Score (0-100)</Label>
        <Input
          id="overall_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.overall_score}
          onChange={(e) => setData({ ...data, overall_score: e.target.value })}
          placeholder="Enter overall score"
        />
      </div>
      <div>
        <Label htmlFor="environmental_score">Environmental Score (0-100)</Label>
        <Input
          id="environmental_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.environmental_score}
          onChange={(e) => setData({ ...data, environmental_score: e.target.value })}
          placeholder="Enter environmental score"
        />
      </div>
      <div>
        <Label htmlFor="social_score">Social Score (0-100)</Label>
        <Input
          id="social_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.social_score}
          onChange={(e) => setData({ ...data, social_score: e.target.value })}
          placeholder="Enter social score"
        />
      </div>
      <div>
        <Label htmlFor="governance_score">Governance Score (0-100)</Label>
        <Input
          id="governance_score"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={data.governance_score}
          onChange={(e) => setData({ ...data, governance_score: e.target.value })}
          placeholder="Enter governance score"
        />
      </div>
      <div>
        <Label htmlFor="follow_up_date">Follow-up Date</Label>
        <Input
          id="follow_up_date"
          type="date"
          value={data.follow_up_date}
          onChange={(e) => setData({ ...data, follow_up_date: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={data.status} onValueChange={(value) => setData({ ...data, status: value })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    {/* Governance & Policy Controls */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="flex items-center gap-3">
        <Switch checked={data.has_ems} onCheckedChange={(checked) => setData({ ...data, has_ems: checked })} />
        <Label>Environmental Management System (ISO 14001)</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={data.policies_environmental} onCheckedChange={(checked) => setData({ ...data, policies_environmental: checked })} />
        <Label>Environmental Policy</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={data.policies_human_rights} onCheckedChange={(checked) => setData({ ...data, policies_human_rights: checked })} />
        <Label>Human Rights Policy</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={data.policies_anti_corruption} onCheckedChange={(checked) => setData({ ...data, policies_anti_corruption: checked })} />
        <Label>Anti-Corruption Policy</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={data.data_privacy_policy} onCheckedChange={(checked) => setData({ ...data, data_privacy_policy: checked })} />
        <Label>Data Privacy Policy</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={data.whistleblower_mechanism} onCheckedChange={(checked) => setData({ ...data, whistleblower_mechanism: checked })} />
        <Label>Whistleblower Mechanism</Label>
      </div>
    </div>

    {/* Climate & Disclosure */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="flex items-center gap-3">
        <Switch checked={data.sbti_committed} onCheckedChange={(checked) => setData({ ...data, sbti_committed: checked })} />
        <Label>SBTi Committed</Label>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={data.ghg_disclosed} onCheckedChange={(checked) => setData({ ...data, ghg_disclosed: checked })} />
        <Label>GHG Emissions Disclosed</Label>
      </div>
      <div>
        <Label>EcoVadis Score</Label>
        <Input value={data.ecovadis_score} onChange={(e) => setData({ ...data, ecovadis_score: e.target.value })} placeholder="0-100" />
      </div>
      <div>
        <Label>CDP Score</Label>
        <Select value={data.cdp_score} onValueChange={(value) => setData({ ...data, cdp_score: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {['A', 'A-', 'B', 'B-', 'C', 'C-', 'D', 'D-', 'F'].map(g => (
              <SelectItem key={g} value={g}>{g}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    {/* Operational Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <Label>Scope 1 Emissions (tCO2e)</Label>
        <Input value={data.scope1_emissions} onChange={(e) => setData({ ...data, scope1_emissions: e.target.value })} type="number" />
      </div>
      <div>
        <Label>Scope 2 Emissions (tCO2e)</Label>
        <Input value={data.scope2_emissions} onChange={(e) => setData({ ...data, scope2_emissions: e.target.value })} type="number" />
      </div>
      <div>
        <Label>Scope 3 Emissions (tCO2e)</Label>
        <Input value={data.scope3_emissions} onChange={(e) => setData({ ...data, scope3_emissions: e.target.value })} type="number" />
      </div>
      <div>
        <Label>Energy Consumption (MWh)</Label>
        <Input value={data.energy_consumption_mwh} onChange={(e) => setData({ ...data, energy_consumption_mwh: e.target.value })} type="number" />
      </div>
      <div>
        <Label>Water Withdrawal (m3)</Label>
        <Input value={data.water_withdrawal_m3} onChange={(e) => setData({ ...data, water_withdrawal_m3: e.target.value })} type="number" />
      </div>
      <div>
        <Label>Waste Generated (tons)</Label>
        <Input value={data.waste_generated_tons} onChange={(e) => setData({ ...data, waste_generated_tons: e.target.value })} type="number" />
      </div>
    </div>

    {/* Incidents & CAP */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label>Incidents Count (H&S, Environmental)</Label>
        <Input value={data.incidents_count} onChange={(e) => setData({ ...data, incidents_count: e.target.value })} type="number" />
      </div>
      <div>
        <Label>Corrective Action Plan in Place</Label>
        <div className="flex items-center gap-3">
          <Switch checked={data.cap_in_place} onCheckedChange={(checked) => setData({ ...data, cap_in_place: checked })} />
          <Input placeholder="CAP due date" type="date" value={data.cap_due_date} onChange={(e) => setData({ ...data, cap_due_date: e.target.value })} />
        </div>
      </div>
    </div>

    <div>
      <Label htmlFor="incidents_details">Incidents Details</Label>
      <Textarea id="incidents_details" value={data.incidents_details} onChange={(e) => setData({ ...data, incidents_details: e.target.value })} rows={3} placeholder="Describe any incidents and responses" />
    </div>

    <div>
      <Label htmlFor="findings">Key Findings</Label>
      <Textarea
        id="findings"
        value={data.findings}
        onChange={(e) => setData({ ...data, findings: e.target.value })}
        placeholder="Document key findings from the assessment"
        rows={3}
      />
    </div>

    <div>
      <Label htmlFor="recommendations">Recommendations</Label>
      <Textarea
        id="recommendations"
        value={data.recommendations}
        onChange={(e) => setData({ ...data, recommendations: e.target.value })}
        placeholder="Provide recommendations for improvement"
        rows={3}
      />
    </div>

    <div>
      <Label htmlFor="action_items">Action Items</Label>
      <Textarea
        id="action_items"
        value={data.action_items}
        onChange={(e) => setData({ ...data, action_items: e.target.value })}
        placeholder="List specific action items to be completed"
        rows={3}
      />
    </div>

    <div>
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={data.notes}
        onChange={(e) => setData({ ...data, notes: e.target.value })}
        placeholder="Additional notes or context"
        rows={3}
      />
    </div>

    {/* Custom Questions */}
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Custom Questions</Label>
      </div>
      {customQuestions && customQuestions.length > 0 && (
        <div className="space-y-3">
          {customQuestions.map((q: any, idx: number) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <div>
                <Label>{q.label}{q.required ? ' *' : ''}</Label>
                {q.type === 'text' && (
                  <Input value={q.value || ''} onChange={(e) => {
                    const arr = [...customQuestions];
                    arr[idx].value = e.target.value;
                    onCustomQuestionsChange(arr);
                  }} />
                )}
                {q.type === 'number' && (
                  <Input type="number" value={q.value || ''} onChange={(e) => {
                    const arr = [...customQuestions];
                    arr[idx].value = e.target.value;
                    onCustomQuestionsChange(arr);
                  }} />
                )}
                {q.type === 'date' && (
                  <Input type="date" value={q.value || ''} onChange={(e) => {
                    const arr = [...customQuestions];
                    arr[idx].value = e.target.value;
                    onCustomQuestionsChange(arr);
                  }} />
                )}
                {q.type === 'boolean' && (
                  <div className="flex items-center gap-2">
                    <Switch checked={!!q.value} onCheckedChange={(checked) => {
                      const arr = [...customQuestions];
                      arr[idx].value = checked;
                      onCustomQuestionsChange(arr);
                    }} />
                    <span>{q.value ? 'Yes' : 'No'}</span>
                  </div>
                )}
                {q.type === 'select' && (
                  <Select value={q.value || ''} onValueChange={(value) => {
                    const arr = [...customQuestions];
                    arr[idx].value = value;
                    onCustomQuestionsChange(arr);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {(q.options || '').split(',').map((opt: string) => opt.trim()).filter(Boolean).map((opt: string) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  const arr = [...customQuestions];
                  arr.splice(idx, 1);
                  onCustomQuestionsChange(arr);
                }}>Remove</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new question */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label>Question Label</Label>
          <Input value={newQuestion.label} onChange={(e) => setNewQuestion({ ...newQuestion, label: e.target.value })} />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={newQuestion.type} onValueChange={(value) => setNewQuestion({ ...newQuestion, type: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="date">Date</SelectItem>
              <SelectItem value="boolean">Yes/No</SelectItem>
              <SelectItem value="select">Select</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Options (comma separated)</Label>
          <Input value={newQuestion.options} onChange={(e) => setNewQuestion({ ...newQuestion, options: e.target.value })} placeholder="For select type" />
        </div>
        <div className="flex items-end gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={newQuestion.required} onCheckedChange={(checked) => setNewQuestion({ ...newQuestion, required: checked })} />
            <Label>Required</Label>
          </div>
          <Button type="button" onClick={() => {
            if (!newQuestion.label) return;
            onCustomQuestionsChange([...(customQuestions || []), { ...newQuestion, value: '' }]);
            setNewQuestion({ label: '', type: 'text', options: '', required: false });
          }}>Add Question</Button>
        </div>
      </div>
    </div>
  </div>
);